
import * as Comlink from 'comlink';

import { DateTime, Duration, Interval } from 'luxon';
import { Filter } from './Filter';
import { Partitioner } from './Partitioner';
import { IntervalLike, SelectionInterval } from './SelectionInterval';

import {TornadoCollectionWorker} from './TorCollection.worker';
import { TornadoGeoJSON, TornadoGeoJSONMin, TornadoProperties } from './tornado';

const worker = new Worker(new URL('./TorCollection.worker', import.meta.url));
const tc_worker = Comlink.wrap<TornadoCollectionWorker>(worker);

class TornadoCollection {
    key: string;
    
    constructor(key: string) {
        this.key = key;
    }

    async getNTornadoes(subintvs: (IntervalLike | SelectionInterval)[], partitioner?: Partitioner, with_time_unknown?: boolean) {
        const packSubInterval = (intv: (IntervalLike | SelectionInterval)) => {
            if (intv instanceof Interval) {
                return 'Interval|' + intv.toISO();
            }
            return intv.toRepr();
        }

        const subintv_strs = subintvs.map(packSubInterval);
        const partitioner_str = partitioner === undefined ? undefined : partitioner.toRepr();

        return await tc_worker.getNTornadoes(this.key, subintv_strs, partitioner_str, with_time_unknown);
    }

    async getGeoJSON(interval: SelectionInterval) : Promise<Record<string, TornadoGeoJSONMin>>;
    async getGeoJSON(interval: SelectionInterval, partitioner: undefined, mini_props: false) : Promise<TornadoGeoJSON>;
    async getGeoJSON(interval: SelectionInterval, partitioner: Partitioner, mini_props: false) : Promise<Record<string, TornadoGeoJSON>>;
    async getGeoJSON(interval: SelectionInterval, partitioner: Partitioner) : Promise<Record<string, TornadoGeoJSONMin>>;
    async getGeoJSON(interval: SelectionInterval, partitioner: undefined, mini_props: true) : Promise<TornadoGeoJSONMin>;
    async getGeoJSON(interval: SelectionInterval, partitioner: Partitioner, mini_props: true) : Promise<Record<string, TornadoGeoJSONMin>>;
    async getGeoJSON(interval: SelectionInterval, partitioner?: Partitioner, mini_props?: boolean) : Promise<unknown> {
        const interval_str = interval.toRepr();
        const partitioner_str = partitioner === undefined ? undefined : partitioner.toRepr();

        const geojson = await tc_worker.getGeoJSON(this.key, interval_str, partitioner_str, mini_props);

        if (partitioner === undefined) {
            return geojson['count'];
        }
        return geojson;
    }

    async getGeoJSONById(id: string) {
        const geojson = await tc_worker.getGeoJSONById(this.key, id);
        if (geojson === null) {
            throw `Tornado with id '${id}' not found in the tornado collection`;
        }
        
        return geojson;
    }

    async getPropertyValues(property: keyof TornadoProperties, interval: SelectionInterval, partitioner?: Partitioner) {
        const interval_str = interval.toRepr();
        const partitioner_str = partitioner === undefined ? undefined : partitioner.toRepr();

        return await tc_worker.getPropertyValues(this.key, property, interval_str, partitioner_str);
    }

    async applyFilters(filters: Filter[]) {
        const filter_strs = Object.values(filters).map(filt => filt.toRepr());
        const key = await tc_worker.applyFilters(this.key, filter_strs);

        return new TornadoCollection(key);
    }

    async getRandomDate(cday_offset: Duration) {
        const dt = await tc_worker.getRandomDate(this.key, cday_offset.toISO());
        return DateTime.fromISO(dt, {'zone': 'utc'});
    }

    static async fromServer(url: string) {
        const key = await tc_worker.downloadFile(url);
        return new TornadoCollection(key);
    }

    deleteInstance() {
        tc_worker.deleteInstance(this.key);
    }
}

export default TornadoCollection;
